import { useMutation, useQueryClient } from "@tanstack/react-query";
import { data, type Schemas } from "../api/client";
import { Page, Section, useApiQuery } from "../app/Page";
import { useBff, useCan, useMe } from "../app/session";
import { useT } from "../i18n";
import { ErrorState, Empty } from "../ui/states";
import { Table } from "../ui/Table";

type Trunk = Schemas["TrunkOut"];
type Profile = Schemas["ProfileOut"];

/** A recording source's assist profile (V1-E11-T04): the calls it sends are helped by it, and reviewed by it. */
function AssistProfile({ trunk, profiles }: { trunk: Trunk; profiles: Profile[] | undefined }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const canWrite = useCan("telephony:write");
  const ingest = trunk.siprec_ingest;
  const set = useMutation({
    mutationFn: (profile: string | null) => data(api.PATCH("/v1/sip-trunks/{trunk_id}", {
      params: { path: { trunk_id: trunk.id } },
      body: { siprec_ingest: { app_id: ingest?.app_id ?? "", first: ingest?.first as "customer" | "agent", assist_profile: profile } },
    })),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["sip-trunks", tenant] }),
  });
  if (!ingest) return <span className="text-[var(--color-muted)]">{t("trunks.notRecording")}</span>;
  const current = ingest.assist_profile ?? "";
  const name = profiles?.find((p) => p.id === current)?.name ?? current;
  if (!canWrite || !profiles) return <span>{current ? name : t("trunks.noAssist")}</span>;
  return (
    <span className="flex flex-col gap-1">
      <select aria-label={t("trunks.assistFor", { name: trunk.name })} value={current} disabled={set.isPending}
        onChange={(e) => set.mutate(e.target.value || null)}
        className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-sm">
        <option value="">{t("trunks.noAssist")}</option>
        {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {set.isError && <ErrorState error={set.error} />}
    </span>
  );
}

export function TrunksPage() {
  const t = useT();
  const canReadProfiles = useCan("agents:read");
  const trunks = useApiQuery("sip-trunks", (api) => api.GET("/v1/sip-trunks", { params: { query: { limit: 100 } } }));
  const profiles = useApiQuery("assist-profiles", (api) => api.GET("/v1/assist-profiles"),
    { enabled: canReadProfiles });
  return (
    <Page title={t("nav.trunks")} description={t("trunks.description")}>
      <Section query={trunks}>
        {(page) =>
          page.data.length === 0 ? (
            <Empty title={t("trunks.empty")}>
              <p>{t("trunks.emptyBody")}</p>
            </Empty>
          ) : (
            <Table
              caption={t("nav.trunks")}
              rows={page.data}
              rowKey={(tr) => tr.id}
              columns={[
                { header: t("trunks.col.name"), cell: (tr) => <span className="font-medium">{tr.name}</span> },
                { header: t("trunks.col.direction"), cell: (tr) => tr.direction },
                { header: t("trunks.col.auth"), cell: (tr) => tr.auth_mode },
                { header: t("trunks.col.enabled"), cell: (tr) => t(tr.enabled ? "common.yes" : "common.no") },
                { header: t("trunks.col.assist"), cell: (tr) => <AssistProfile trunk={tr} profiles={profiles.data?.data} /> },
              ]}
            />
          )
        }
      </Section>
    </Page>
  );
}
